import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import { fetchCustomer } from "../../api/customers";
import { fetchPlaiditems } from "../../api/plaid";
import { useCurrentProfile } from "../../ContextWrapper";
import ClientDashboardLayout from "./ClientDashboardLayout";

const LABEL_STYLE = {
  color: "var(--text-secondary, rgba(0, 0, 0, 0.60))",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontFamily: "var(--fontFamily, \"Rethink Sans\")",
  fontSize: "var(--font-size-0875-rem, 14px)",
  fontWeight: "var(--fontWeightRegular, 400)",
  lineHeight: "143%",
  letterSpacing: 0.17,
};

const BODY_STYLE = {
  color: "var(--text-primary, rgba(0, 0, 0, 0.87))",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontFamily: "var(--fontFamily, \"Rethink Sans\")",
  fontSize: "var(--font-size-09375-rem, 15px)",
  fontWeight: "var(--fontWeightMedium, 500)",
  lineHeight: "150%",
  letterSpacing: 0.15,
};

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const FieldRow = ({ label, value }) => (
  <Box sx={{ py: 1.25 }}>
    <Typography variant="caption" sx={LABEL_STYLE}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ ...BODY_STYLE, mt: 0.25 }}>
      {formatValue(value)}
    </Typography>
  </Box>
);

const PillFieldRow = ({ label, values }) => (
  <Box sx={{ py: 1.25 }}>
    <Typography variant="caption" sx={LABEL_STYLE}>
      {label}
    </Typography>
    <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {(values || []).length > 0 ? (
        values.map((value) => <Chip key={value} label={value} size="small" sx={{ "& .MuiChip-label": BODY_STYLE }} />)
      ) : (
        <Typography variant="body2" sx={BODY_STYLE}>
          -
        </Typography>
      )}
    </Box>
  </Box>
);

const SectionCard = ({ title, subtitle, iconPath, children }) => (
  <Card
    sx={{
      borderRadius: 2,
      border: "1px solid #E5E7EB",
      boxShadow: "0 10px 20px rgba(15,23,42,0.05)",
      overflow: "hidden",
    }}
  >
    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {iconPath ? (
          <Box
            component="img"
            src={iconPath}
            alt={`${title} icon`}
            sx={{ width: 22, height: 22, objectFit: "contain" }}
          />
        ) : null}
        <Typography variant="h6" sx={LABEL_STYLE}>
          {title}
        </Typography>
      </Box>
      {subtitle ? (
        <Typography variant="body2" sx={{ ...LABEL_STYLE, mb: 2 }}>
          {subtitle}
        </Typography>
      ) : null}
      <Divider sx={{ mb: 1 }} />
      {children}
    </CardContent>
  </Card>
);

const STATUS_META = {
  csv: {
    label: "CSV",
    color: "#0288D1",
    iconPath: "/csv.svg",
  },
  plaid: {
    label: "Plaid",
    color: "#2E7D32",
    iconPath: "/plaid.svg",
  },
};

const PERFORMANCE_TABS = [
  { id: "team", label: "Team overview" },
  { id: "capacity", label: "Capacity overview" },
  { id: "drivers", label: "Performance drivers" },
];

export default function ProfileOverviewPage() {
  const { profile } = useCurrentProfile();
  const { customer_id: routeCustomerId } = useParams();
  const [plaidItems, setPlaidItems] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [fetchedCustomer, setFetchedCustomer] = useState(null);
  const [performanceTab, setPerformanceTab] = useState("team");

  // Try to find the customer in the logged-in user's customer list first.
  const profileCustomer = useMemo(() => {
    const customerList = Array.isArray(profile?.customers) ? profile.customers : [];
    if (routeCustomerId) {
      return customerList.find((c) => String(c.id) === String(routeCustomerId)) || null;
    }
    // No id in URL — fall back to selectedCustomerId in storage.
    const storedId = sessionStorage.getItem("selectedCustomerId");
    const selected = storedId
      ? customerList.find((c) => String(c.id) === String(storedId))
      : null;
    return selected || customerList.find((c) => !c?.is_demo) || customerList[0] || null;
  }, [profile, routeCustomerId]);

  // When the URL has a customer_id not in the profile's list (e.g. staff view),
  // fetch the customer directly from the API.
  useEffect(() => {
    if (!routeCustomerId || profileCustomer) {
      setFetchedCustomer(null);
      return;
    }
    let isCancelled = false;
    setLoadingCustomer(true);
    fetchCustomer(routeCustomerId)
      .then((data) => { if (!isCancelled) setFetchedCustomer(data || null); })
      .catch(() => { if (!isCancelled) setFetchedCustomer(null); })
      .finally(() => { if (!isCancelled) setLoadingCustomer(false); });
    return () => { isCancelled = true; };
  }, [routeCustomerId, profileCustomer]);

  const activeCustomer = fetchedCustomer || profileCustomer;

  const address = useMemo(() => {
    if (!activeCustomer) return "-";
    const parts = [activeCustomer.address1, activeCustomer.address2].filter(Boolean);
    return parts.length ? parts.join(", ") : "-";
  }, [activeCustomer]);

  useEffect(() => {
    let isCancelled = false;
    const customerId = activeCustomer?.id;

    if (!customerId) {
      setPlaidItems([]);
      return;
    }

    const loadAccounts = async () => {
      setLoadingAccounts(true);
      try {
        const response = await fetchPlaiditems(customerId);
        if (!isCancelled) {
          setPlaidItems(Array.isArray(response) ? response : []);
        }
      } catch (error) {
        if (!isCancelled) {
          setPlaidItems([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingAccounts(false);
        }
      }
    };

    loadAccounts();
    return () => {
      isCancelled = true;
    };
  }, [activeCustomer?.id]);

  const formatLastSync = (dateValue) => {
    if (!dateValue) return "-";
    try {
      const d = new Date(dateValue);
      return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString(undefined, { dateStyle: "medium" });
    } catch {
      return "-";
    }
  };

  const connectedAccounts = useMemo(() => {
    return (Array.isArray(plaidItems) ? plaidItems : [])
      .filter((item) => item.product === "transactions")
      .flatMap((item) =>
        (item.bankaccount_set || []).map((account) => ({
          key: `${item.id}-${account.id}`,
          bankName: item.name || "-",
          accountName: account.name || "-",
          statusType: Boolean(item.is_fake_import) ? "csv" : "plaid",
          lastSynced: formatLastSync(item.last_sync_date),
        }))
      );
  }, [plaidItems]);

  if (loadingCustomer) {
    return (
      <ClientDashboardLayout title="Profile Overview" contentMaxWidth={1400} contentAlign="center">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      </ClientDashboardLayout>
    );
  }

  return (
    <ClientDashboardLayout title="Profile Overview" contentMaxWidth={1400} contentAlign="center">
      <Stack spacing={2.5}>
        <SectionCard
          title="Practice Overview"
          iconPath="/Practice-overview.svg"
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: 4,
              rowGap: 3,
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <FieldRow label="Business Name" value={activeCustomer?.name} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldRow label="Business Type" value={activeCustomer?.business_type} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <PillFieldRow
                label="Business Niches"
                values={activeCustomer?.business_niches ?? []}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldRow label="Address" value={address} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldRow label="Zip Code" value={activeCustomer?.zip_code} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldRow label="City" value={activeCustomer?.city} />
            </Box>
            <Box sx={{ minWidth: 0, gridColumn: { xs: "1", sm: "1 / -1" } }}>
              <FieldRow
                label="Business Mode"
                value={activeCustomer?.business_mode}
              />
            </Box>
          </Box>
        </SectionCard>

        <SectionCard
          title="Performance Overview"
          iconPath="/Performance-overview.svg"
          subtitle="Performance and capacity details captured during setup."
        >
          <Box
            sx={{
              bgcolor: "#F4F4F6",
              borderRadius: "8px",
              px: "6px",
              py: "4px",
              display: "inline-flex",
              alignItems: "stretch",
              justifyContent: "center",
              minWidth: 0,
              width: "100%",
              mb: 2,
            }}
          >
            {PERFORMANCE_TABS.map((tab) => (
              <ButtonBase
                key={tab.id}
                onClick={() => setPerformanceTab(tab.id)}
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                  borderRadius: "8px",
                  py: "4px",
                  px: { xs: 1.5, sm: 2, md: 2 },
                  bgcolor: performanceTab === tab.id ? "#FFFFFF" : "transparent",
                  boxShadow:
                    performanceTab === tab.id
                      ? "0px 1px 2px 0px rgba(0,0,0,0.05)"
                      : "none",
                  transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Rethink Sans', 'Inter', sans-serif",
                    fontSize: { xs: 13, sm: 14 },
                    fontWeight: performanceTab === tab.id ? 600 : 500,
                    lineHeight: "20px",
                    color: "#262A2C",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {tab.label}
                </Typography>
              </ButtonBase>
            ))}
          </Box>

          {performanceTab === "team" && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 4,
                rowGap: 3,
                width: "100%",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="Does your clinic accept insurance?"
                  value={
                    activeCustomer?.accepts_insurance === null ||
                    activeCustomer?.accepts_insurance === undefined
                      ? "-"
                      : activeCustomer?.accepts_insurance
                        ? "Yes"
                        : "No"
                  }
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="Do you see patients yourself?"
                  value={
                    activeCustomer?.sees_patients_in_person === null ||
                    activeCustomer?.sees_patients_in_person === undefined
                      ? "-"
                      : activeCustomer?.sees_patients_in_person
                        ? "Yes"
                        : "No"
                  }
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="How many people work in your business?"
                  value={activeCustomer?.people_at_business}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="How many are healthcare providers?"
                  value={activeCustomer?.health_care_providers}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="How many are support or admin staff?"
                  value={activeCustomer?.staff}
                />
              </Box>
            </Box>
          )}

          {performanceTab === "capacity" && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 4,
                rowGap: 3,
                width: "100%",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="How many patients do you typically see each week?"
                  value={activeCustomer?.weekly_patients}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="On average, how many are new patients each week?"
                  value={activeCustomer?.average_weekly_patients}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="At full capacity, how many patients could your team handle weekly?"
                  value={activeCustomer?.handleable_patients_weekly}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldRow
                  label="At full capacity, what's the most new patients you could handle weekly?"
                  value={activeCustomer?.handleable_new_patients_weekly}
                />
              </Box>
            </Box>
          )}

          {performanceTab === "drivers" && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 4,
                rowGap: 3,
                width: "100%",
              }}
            >
              <Box sx={{ minWidth: 0, gridColumn: { xs: "1", sm: "1 / -1" } }}>
                <Typography variant="caption" sx={LABEL_STYLE}>
                  Which marketing efforts are you actively using?
                </Typography>
                <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {(activeCustomer?.marketing_fields || []).length > 0 ? (
                    (activeCustomer?.marketing_fields || []).map((field) => (
                      <Chip key={field} label={field} size="small" sx={{ "& .MuiChip-label": BODY_STYLE }} />
                    ))
                  ) : (
                    <Typography variant="body2" sx={BODY_STYLE}>
                      -
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ minWidth: 0, gridColumn: { xs: "1", sm: "1 / -1" } }}>
                <FieldRow
                  label="Additional Information"
                  value={activeCustomer?.added_business_context}
                />
              </Box>
            </Box>
          )}
        </SectionCard>

        <SectionCard
          title="Connected Accounts"
          iconPath="/Linked-accounts.svg"
          subtitle="Bank and connected accounts linked during setup."
        >
          {loadingAccounts ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : connectedAccounts.length === 0 ? (
            <Typography variant="body2" sx={{ ...BODY_STYLE, py: 1.25 }}>
              No connected accounts found.
            </Typography>
          ) : (
            <TableContainer
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table size="small" sx={{ minWidth: 320 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F4F4F6" }}>
                    <TableCell sx={{ ...LABEL_STYLE, py: 1.25 }}>Bank Name</TableCell>
                    <TableCell sx={{ ...LABEL_STYLE, py: 1.25 }}>Account Name</TableCell>
                    <TableCell sx={{ ...LABEL_STYLE, py: 1.25 }}>Status</TableCell>
                    <TableCell sx={{ ...LABEL_STYLE, py: 1.25 }}>Last Synced</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connectedAccounts.map((account) => (
                    <TableRow key={account.key} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell sx={{ ...BODY_STYLE, py: 1.25 }}>{account.bankName}</TableCell>
                      <TableCell sx={{ ...BODY_STYLE, py: 1.25 }}>{account.accountName}</TableCell>
                      <TableCell sx={{ ...BODY_STYLE, py: 1.25 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.25,
                            py: 0.375,
                            borderRadius: "999px",
                            bgcolor: alpha(
                              STATUS_META[account.statusType]?.color || "#2E7D32",
                              0.08
                            ),
                            color: STATUS_META[account.statusType]?.color || "#2E7D32",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "var(--fontFamily, \"Rethink Sans\")",
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            {STATUS_META[account.statusType]?.label || "Plaid"}
                          </Typography>
                          <Box
                            component="img"
                            src={STATUS_META[account.statusType]?.iconPath || "/plaid.svg"}
                            alt=""
                            sx={{ width: 14, height: 14, objectFit: "contain" }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...BODY_STYLE, py: 1.25 }}>
                        {account.lastSynced}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Stack>
    </ClientDashboardLayout>
  );
}
